import { hasAnyAccessToResource, hasViewAccessToResource } from "api/general";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { INFORMATION } from "contexts/Informations/utils";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import {
  pushInformation,
  removeInformation,
} from "reduxState/informations/informationsSlice";
import { checkPathsMatches, links } from "utilities/links";

export const useAccessToast = () => {
  const location = useLocation();
  const appDispatch = useAppDispatch();
  const currentStore = useAppSelector(selectCurrentStoreData);

  const hasViewAdminPermission = hasViewAccessToResource("ADMIN");
  const hasAnyCustomEventPermission = hasAnyAccessToResource("CUSTOM_EVENT");
  const hasViewCustomEventPermission = hasViewAccessToResource("CUSTOM_EVENT");
  const hasAnyTierPermission = hasAnyAccessToResource("LEVEL");
  const hasViewTierPermission = hasViewAccessToResource("LEVEL");
  const hasViewCampaignPermission = hasViewAccessToResource("CAMPAIGN");
  const hasAnyChannelPermission = hasAnyAccessToResource("CHANNEL");
  const hasViewChannelPermission = hasViewAccessToResource("CHANNEL");
  const hasViewRolePermission = hasViewAccessToResource("ACL");
  const hasAnyLanguagePermission = hasAnyAccessToResource("LANGUAGE");
  const hasViewLanguagePermission = hasViewAccessToResource("LANGUAGE");

  const limitedAccessPages = [
    {
      path: links.achievementsList,
      showToast: hasViewCampaignPermission || !hasAnyLanguagePermission,
    },
    {
      path: links.addAchievement,
      showToast: !hasAnyCustomEventPermission || !hasAnyChannelPermission,
    },
    {
      path: links.editAchievement,
      showToast: !hasAnyCustomEventPermission || !hasAnyChannelPermission,
    },
    {
      path: links.achievementsList,
      showToast: !hasAnyLanguagePermission,
    },
    // Admins
    {
      path: links.adminData,
      showToast: hasViewAdminPermission,
    },
    {
      path: links.editAdmin,
      showToast: hasViewAdminPermission,
    },
    // Campaigns
    {
      path: links.campaignsList,
      showToast: hasViewCampaignPermission || !hasAnyLanguagePermission,
    },
    {
      path: links.addCampaign,
      showToast: !hasAnyCustomEventPermission || !hasAnyChannelPermission,
    },
    {
      path: links.editCampaign,
      showToast:
        !hasAnyCustomEventPermission ||
        !hasAnyTierPermission ||
        !hasAnyChannelPermission,
    },
    {
      path: links.simulateCampaign,
      showToast:
        !hasAnyCustomEventPermission ||
        !hasAnyChannelPermission ||
        !hasAnyLanguagePermission,
    },
    // Channels
    {
      path: links.settings,
      showToast: hasViewChannelPermission,
    },
    {
      path: links.editChannel,
      showToast: hasViewChannelPermission,
    },
    // Custom Event Schemas
    {
      path: links.customEventsSchemaList,
      showToast: hasViewCustomEventPermission,
    },
    // Languages
    {
      path: links.editLanguage,
      showToast: hasViewLanguagePermission,
    },
    {
      path: links.settings,
      showToast: hasViewLanguagePermission,
    },
    // Members
    {
      path: links.membersList,
      showToast: !hasAnyTierPermission,
    },
    {
      path: links.addMember,
      showToast: !hasAnyTierPermission,
    },
    {
      path: links.editMember,
      showToast: !hasAnyTierPermission,
    },
    // RewardCategories
    {
      path: links.rewardCategoriesList,
      showToast: !hasAnyLanguagePermission,
    },
    // Rewards
    {
      path: links.addReward,
      showToast: !hasAnyTierPermission,
    },
    {
      path: links.editReward,
      showToast: !hasAnyTierPermission,
    },
    {
      path: links.rewardsList,
      showToast: !hasAnyLanguagePermission,
    },
    {
      path: links.reward,
      showToast: !hasAnyLanguagePermission,
    },
    // Roles
    {
      path: links.settings,
      showToast: hasViewRolePermission,
    },
    {
      path: links.editRole,
      showToast: hasViewRolePermission,
    },
    // Tiers
    {
      path: links.tiersList,
      showToast: hasViewTierPermission || !hasAnyLanguagePermission,
    },
    {
      path: links.editTier,
      showToast: hasViewTierPermission,
    },
    // Transactions
    {
      path: links.addTransaction,
      showToast: !hasAnyChannelPermission,
    },
    {
      path: links.editTransaction,
      showToast: !hasAnyChannelPermission,
    },
    {
      path: links.transactionsList,
      showToast: !hasAnyChannelPermission,
    },
    // Wallet types
    {
      path: links.walletTypesList,
      showToast: !hasAnyLanguagePermission,
    },
  ];

  const findLimitedAccessPage = () => {
    return limitedAccessPages.find((item) =>
      checkPathsMatches(location.pathname, item.path),
    );
  };

  const foundLimitedAccessPage = findLimitedAccessPage();

  // Effect for controlling AccessToast visibility
  useEffect(() => {
    // Don't show toast when tenant is empty
    if (!currentStore.code) {
      appDispatch(removeInformation(INFORMATION.UNAUTHORIZED));
      return;
    }

    if (foundLimitedAccessPage?.showToast) {
      appDispatch(pushInformation(INFORMATION.UNAUTHORIZED));
    } else {
      appDispatch(removeInformation(INFORMATION.UNAUTHORIZED));
    }
  }, [
    location.pathname,
    foundLimitedAccessPage,
    appDispatch,
    currentStore.code,
  ]);
};
